import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { useBranchesStore } from '@/features/branches/stores/branches.store'
import type { BranchId } from '@/features/branches/domain/branch.types'
import { useAuthStore } from '@/stores/auth.store'

const STORAGE_KEY = 'joja.branch-id'

/**
 * The one site the company is running, while it is running one.
 *
 * Chinobod is being set up and the other branches exist only as rows, so a switcher offering them
 * is a way to file a day's work against a hall nobody is standing in. While this names a branch the
 * console pins itself to it — the switcher disappears and every branch-scoped request carries it,
 * whatever a stale `localStorage` says.
 *
 * Set it to `null` to hand the branches back: nothing else has to change.
 */
const PINNED_BRANCH_NAME: string | null = 'Chinobod'

const readPersistedBranchId = (): BranchId | null => {
  const raw = localStorage.getItem(STORAGE_KEY)
  const id = Number(raw)

  return raw && Number.isInteger(id) && id > 0 ? id : null
}

/**
 * The selected hatchery branch — the scope every branch-scoped request is made in.
 *
 * Kept apart from `branches.store` on purpose: that one is master data (the CRUD screen's rows),
 * this one is "which branch am I standing in". Every branch-scoped endpoint requires `branchId` and
 * answers 400 without it, so no scoped view may mount before `ready` (hatchery.md §2).
 */
export const useBranchScopeStore = defineStore('branch-scope', () => {
  const branchesStore = useBranchesStore()
  const authStore = useAuthStore()

  const activeBranchId = ref<BranchId | null>(readPersistedBranchId())
  /** Bumped on every effective branch change: drives store resets, the view remount, and late-write guards. */
  const branchVersion = ref(0)
  /** Bootstrap resolved — either a branch is active, or we know there is none to pick. */
  const ready = ref(false)
  const error = ref<string | null>(null)

  const activeBranch = computed(() => branchesStore.findById(activeBranchId.value) ?? null)
  const activeBranchName = computed(() => activeBranch.value?.name ?? '')
  const hasBranchScope = computed(() => activeBranchId.value !== null)
  /** The console is standing in one named site, so the branch is not the user's to choose. */
  const isPinnedToOneBranch = computed(() => PINNED_BRANCH_NAME !== null)
  const canSwitch = computed(() => !isPinnedToOneBranch.value && branchesStore.sortedBranches.length > 1)
  const canReadBranches = computed(() => authStore.hasPermission('branches:read'))

  const persist = (id: BranchId | null) => {
    if (id === null) localStorage.removeItem(STORAGE_KEY)
    else localStorage.setItem(STORAGE_KEY, String(id))
  }

  /**
   * The branchId for a scoped request. Throws only on a programming error — views are gated on
   * `hasBranchScope`, so a scoped store action can never legitimately run without a scope.
   */
  const requireBranchId = (): BranchId => {
    if (activeBranchId.value === null) {
      throw new Error('No active branch: a branch-scoped request was made outside a branch scope.')
    }

    return activeBranchId.value
  }

  const setActiveBranch = (id: BranchId) => {
    if (activeBranchId.value === id) return

    activeBranchId.value = id
    persist(id)
    branchVersion.value += 1
  }

  /**
   * Reconcile the selection against the list we actually hold: a persisted id that no longer exists
   * (branch deleted elsewhere) silently falls back to the first branch, so a stale localStorage
   * value can never produce `404 branch.not-found`.
   */
  const reconcile = () => {
    const branches = branchesStore.sortedBranches
    // Pinned: that branch wins over anything persisted, since the user has no switcher to correct a
    // stale value with. A name that matches nothing falls through to the ordinary first-branch rule
    // rather than leaving the console with no scope at all.
    const pinnedBranch = PINNED_BRANCH_NAME
      ? branches.find((branch) => branch.name.toLowerCase() === PINNED_BRANCH_NAME.toLowerCase())
      : undefined
    const firstBranch = pinnedBranch ?? branches[0]

    if (!firstBranch) {
      activeBranchId.value = null
      persist(null)
      return
    }

    if (firstBranch.id === activeBranchId.value) return
    if (!pinnedBranch && branches.some((branch) => branch.id === activeBranchId.value)) return

    const fallbackId = firstBranch.id
    const hadSelection = activeBranchId.value !== null
    activeBranchId.value = fallbackId
    persist(fallbackId)
    if (hadSelection) branchVersion.value += 1
  }

  const refreshBranches = async (signal?: AbortSignal) => {
    await branchesStore.fetchBranches(signal)
    reconcile()
  }

  // Single-flight and idempotent, like auth's `ensureSessionLoaded` — the router guard calls this on
  // every navigation. `epoch` invalidates an in-flight run that a reset() has superseded: without it
  // the `.finally` below would re-raise `ready` after a logout, and the next session's bootstrap
  // would short-circuit into the previous user's scope.
  let bootstrapEpoch = 0
  let bootstrapPromise: Promise<void> | null = null
  const bootstrap = () => {
    if (ready.value) return Promise.resolve()
    if (bootstrapPromise) return bootstrapPromise

    const epoch = bootstrapEpoch
    bootstrapPromise = (async () => {
      // Without branches:read we cannot resolve a scope; scoped views show the notice rather than
      // firing a request that is guaranteed to 400.
      if (!canReadBranches.value) {
        activeBranchId.value = null
        error.value = null
        return
      }

      try {
        await refreshBranches()
        if (epoch !== bootstrapEpoch) return
        error.value = null
      } catch (requestError) {
        if (epoch !== bootstrapEpoch) return
        activeBranchId.value = null
        error.value = branchesStore.error ?? String(requestError)
      }
    })().finally(() => {
      if (epoch !== bootstrapEpoch) return
      ready.value = true
      bootstrapPromise = null
    })

    return bootstrapPromise
  }

  /**
   * Retry after a failed bootstrap. Deliberately does NOT lower `ready`: that would reopen the
   * shell's scope gate and mount a branch-scoped view while `activeBranchId` is still null, whose
   * first fetch would then throw out of `requireBranchId()`.
   */
  const retrying = ref(false)
  const retryBootstrap = async () => {
    if (retrying.value) return

    retrying.value = true

    try {
      await refreshBranches()
      error.value = null
    } catch (requestError) {
      activeBranchId.value = null
      error.value = branchesStore.error ?? String(requestError)
    } finally {
      retrying.value = false
    }
  }

  const reset = () => {
    bootstrapEpoch += 1
    bootstrapPromise = null
    activeBranchId.value = null
    persist(null)
    ready.value = false
    error.value = null
    branchVersion.value += 1
  }

  // Logout must drop the scope, or the next user boots into the previous user's branch: `ready`
  // would still be true, so `bootstrap()` would no-op and never re-resolve. Watching the session
  // covers all of it — the logout buttons, the change-password screen, and an expired refresh —
  // without every call site having to remember.
  watch(
    () => authStore.isAuthenticated,
    (isAuthenticated) => {
      if (!isAuthenticated) reset()
    },
  )

  return {
    activeBranchId,
    branchVersion,
    ready,
    retrying,
    error,
    activeBranch,
    activeBranchName,
    hasBranchScope,
    canSwitch,
    isPinnedToOneBranch,
    canReadBranches,
    requireBranchId,
    setActiveBranch,
    refreshBranches,
    bootstrap,
    retryBootstrap,
    reset,
  }
})
